import type { FastifyRequest, FastifyReply } from 'fastify'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import type {
  CreatePlanDTO,
  UpdatePlanDTO,
  CreateStageDTO,
  UpdateStageDTO,
  GeneratePlanDTO,
  SharePlanDTO,
  UpdateSyncStateDTO,
  PlannerPreferences,
  PlanCollaborator
} from '@my-roadtrip/types'
import { PlannerService } from './planner.service'
import { tryGetBillingService } from '../billing/billing.container.js'
import { ApiResponse } from '../../utils/response'
import { appEvents, AppEvent } from '../../core/events/eventEmitter.js'
import { getRedisClient } from '../../core/redis/client.js'
import { sanitizePlanData, sanitizeStageData } from '../../utils/sanitize'
import { checkPremiumStatus } from '../../utils/premium'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('PlannerController')

export class PlannerController {
  constructor(private readonly service: PlannerService) {}

  /**
   * Check if user is the owner of the plan.
   * ONLY owners can delete plans or transfer ownership.
   */
  private isOwner(plan: { userId?: string }, userId?: string | null): boolean {
    if (!userId) return false
    return plan.userId === userId
  }

  private hasViewAccess(
    plan: {
      userId?: string
      sharedWith?: string[]
      sharedPermissions?: Record<string, 'view' | 'edit'>
      collaborators?: Array<string | PlanCollaborator>
    },
    userId?: string | null
  ): boolean {
    if (!userId) return false
    if (plan.userId === userId) return true
    const sharedWith = plan.sharedWith ?? []
    const collaborators = (plan.collaborators ?? []) as Array<string | PlanCollaborator>
    const sharedPermissions = plan.sharedPermissions ?? {}
    const hasCollaboratorAccess = collaborators.some(entry =>
      typeof entry === 'string' ? entry === userId : entry.userId === userId
    )
    return sharedWith.includes(userId) || hasCollaboratorAccess || Boolean(sharedPermissions[userId])
  }

  private hasEditAccess(
    plan: {
      userId?: string
      sharedPermissions?: Record<string, 'view' | 'edit'>
      collaborators?: Array<string | PlanCollaborator>
    },
    userId?: string | null
  ): boolean {
    if (!userId) return false
    if (plan.userId === userId) return true
    const sharedPermissions = plan.sharedPermissions ?? {}
    const collaborators = (plan.collaborators ?? []) as Array<string | PlanCollaborator | string>
    const hasCollaboratorEdit = collaborators.some(entry => {
      if (isString(entry)) {
        // Legacy format: being listed as a collaborator implies edit access
        return entry === userId
      }
      if (entry.userId !== userId) return false
      // New format: owner/editor have edit; if role is missing, keep behavior permissive
      return !entry.role || entry.role === 'owner' || entry.role === 'editor'
    })
    return sharedPermissions[userId] === 'edit' || hasCollaboratorEdit
  }

  async getDashboard(request: FastifyRequest, reply: FastifyReply) {
    const userId = request.user?.uid
    logger.debug('Request: Get dashboard snapshot', { userId })

    try {
      if (!userId) {
        logger.warn('Unauthorized dashboard access attempt')
        return ApiResponse.unauthorized(reply)
      }

      const snapshot = await this.service.getDashboardSnapshot(userId)
      return reply.send(snapshot)
    } catch (error) {
      logger.error('Failed to get dashboard snapshot', { userId, error })
      request.log.error(error, 'Failed to get dashboard snapshot')
      return ApiResponse.internalError(reply)
    }
  }

  async getPreferences(request: FastifyRequest, reply: FastifyReply) {
    const userId = request.user?.uid
    logger.debug('Request: Get planner preferences', { userId })

    try {
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const preferences = await this.service.getPlannerPreferences(userId)
      return reply.send({ preferences })
    } catch (error) {
      logger.error('Failed to load planner preferences', { userId, error })
      request.log.error(error, 'Failed to load planner preferences')
      return ApiResponse.internalError(reply)
    }
  }

  async updatePreferences(
    request: FastifyRequest<{ Body: Partial<PlannerPreferences> }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid
    logger.debug('Request: Update planner preferences', { userId })

    try {
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const preferences = await this.service.updatePlannerPreferences(userId, request.body ?? {})
      return reply.send({ success: true, preferences })
    } catch (error) {
      logger.error('Failed to update planner preferences', { userId, error })
      request.log.error(error, 'Failed to update planner preferences')
      return ApiResponse.internalError(reply)
    }
  }

  /**
   * Check AI rate limit (5 requests per day for free users)
   */
  private async checkAIRateLimit(userId: string): Promise<{ allowed: boolean; remaining: number }> {
    const redis = getRedisClient
    const rateLimitKey = `ai:ratelimit:${userId}:${new Date().toISOString().split('T')[0]}` // Daily key
    logger.debug('Checking AI rate limit', { userId, rateLimitKey })
    
    const count = await redis.incr(rateLimitKey)
    if (count === 1) {
      // First request of the day - set expiry to end of day
      const now = new Date()
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
      const ttl = Math.floor((endOfDay.getTime() - now.getTime()) / 1000)
      await redis.expire(rateLimitKey, ttl)
    }

  const MAX_AI_REQUESTS_PER_DAY = 5
    return {
      allowed: count <= MAX_AI_REQUESTS_PER_DAY,
      remaining: Math.max(0, MAX_AI_REQUESTS_PER_DAY - count)
    }
  }

  async getPublicPlans(request: FastifyRequest, reply: FastifyReply) {
    logger.debug('Request: Get public plans')
    try {
      const plans = await this.service.getPublicPlans()
      
      // 🔒 SECURITY: Remove sensitive fields to prevent data leakage
      const sanitizedPlans = plans.map(plan => {
        const { userId, sharedWith, sharedPermissions, collaborators, ...publicPlan } = plan
        return publicPlan
      })
      
      logger.info('Public plans fetched successfully', { count: sanitizedPlans.length })
      return reply.send(sanitizedPlans)
    } catch (error) {
      logger.error('Failed to get public plans', { error })
      request.log.error(error, 'Failed to get public plans')
      return ApiResponse.internalError(reply)
    }
  }

  async getPlans(request: FastifyRequest, reply: FastifyReply) {
    const userId = request.user?.uid
    logger.debug('Request: Get user plans', { userId })
    
    try {
      if (!userId) {
        logger.warn('Unauthorized: No user ID in request')
        return ApiResponse.unauthorized(reply)
      }

      const plans = await this.service.getUserPlans(userId)
      logger.info('User plans fetched successfully', { userId, count: plans.length })
      return reply.send(plans)
    } catch (error) {
      logger.error('Failed to get plans', { userId, error })
      request.log.error(error, 'Failed to get plans')
      return ApiResponse.internalError(reply)
    }
  }

  async getCurrentPlan(request: FastifyRequest, reply: FastifyReply) {
    const userId = request.user?.uid
    logger.debug('Request: Get current plan', { userId })

    try {
      if (!userId) {
        logger.warn('Unauthorized: No user ID in request')
        return ApiResponse.unauthorized(reply)
      }

      const plan = await this.service.getCurrentPlan(userId)
      logger.info('Current plan fetched successfully', { userId, planId: plan.id })
      return reply.send(plan)
    } catch (error) {
      logger.error('Failed to get current plan', { userId, error })
      request.log.error(error, 'Failed to get current plan')
      return ApiResponse.internalError(reply)
    }
  }

  async getPlan(request: FastifyRequest<{ Params: { id: string } }>, reply: FastifyReply) {
    const { id } = request.params
    const userId = request.user?.uid
    logger.debug('Request: Get plan', { planId: id, userId })
    
    try {
      const plan = await this.service.getPlan(id)

      if (!plan) {
        logger.warn('Plan not found', { planId: id, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      // Check access (owner, shared, or collaborator)
      if (!this.hasViewAccess(plan, userId)) {
        logger.warn('Forbidden: User does not have access to plan', { planId: id, userId, ownerId: plan.userId })
        return ApiResponse.forbidden(reply)
      }

      logger.info('Plan fetched successfully', { planId: id, userId, stagesCount: plan.stages?.length || 0 })
      return reply.send(plan)
    } catch (error) {
      logger.error('Failed to get plan', { planId: id, userId, error })
      request.log.error(error, 'Failed to get plan')
      return ApiResponse.internalError(reply)
    }
  }

  async createPlan(request: FastifyRequest<{ Body: CreatePlanDTO }>, reply: FastifyReply) {
    const userId = request.user?.uid
    logger.debug('Request: Create plan', { userId, planName: request.body.name })
    const startedAt = Date.now()
    
    try {
      if (!userId) {
        logger.warn('Unauthorized: No user ID in request')
        return ApiResponse.unauthorized(reply)
      }

      // E2E bypass: skip quota check for test user
      const isE2EUser = userId === 'codex-e2e'

  // Check max plans limit (even without billing service)
  const MAX_FREE_PLANS = 100 // increased 10x
      const userPlans = await this.service.getUserPlans(userId)
      logger.debug('Checking plan limit', { userId, currentPlans: userPlans.length, maxPlans: MAX_FREE_PLANS })
      
      const billingService = tryGetBillingService()
      if (billingService && !isE2EUser) {
        const quotaCheck = await billingService.checkAndConsumeQuota(userId, 'create_plan', 1, false)
        if (!quotaCheck.allowed) {
          logger.warn('Plan quota exceeded via billing service', {
            userId,
            remaining: quotaCheck.remaining ?? null,
            currentPlans: userPlans.length
          })
          return ApiResponse.tooManyRequests(reply, 'Plan quota exceeded')
        }
        // Sanitize input before saving
        const sanitizedData = sanitizePlanData(request.body)
        const plan = await this.service.createPlan(sanitizedData, userId)
        await billingService.checkAndConsumeQuota(userId, 'create_plan', 1, true)
        logger.info('Plan created via controller', {
          userId,
          planId: plan.id,
          stagesCount: plan.stages?.length ?? 0,
          durationMs: Date.now() - startedAt,
          billing: 'managed'
        })
        return reply.code(201).send(plan)
      }

      // Free user limit (no billing service)
      if (userPlans.length >= MAX_FREE_PLANS) {
        logger.warn('Plan limit reached for free user', { userId, maxPlans: MAX_FREE_PLANS })
        return ApiResponse.tooManyRequests(reply, `Maximum number of plans reached (${MAX_FREE_PLANS} for free users)`)
      }

      // Sanitize input before saving
      const sanitizedData = sanitizePlanData(request.body)
      const plan = await this.service.createPlan(sanitizedData, userId)
      logger.info('Plan created via controller', {
        userId,
        planId: plan.id,
        stagesCount: plan.stages?.length ?? 0,
        durationMs: Date.now() - startedAt,
        billing: 'free'
      })
      
      // Emit event for gamification
      appEvents.emit(AppEvent.PLAN_CREATED, { userId, planId: plan.id, timestamp: Date.now() })
      
      return reply.code(201).send(plan)
    } catch (error) {
      logger.error('Failed to create plan via controller', { userId, error })
      request.log.error(error, 'Failed to create plan')
      return ApiResponse.internalError(reply)
    }
  }

  async generatePlan(request: FastifyRequest<{ Body: GeneratePlanDTO }>, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      logger.debug('Request: Generate AI plan', { userId })

      // Check if user has premium subscription (premium users have unlimited AI generation)
      const { isPremium } = await checkPremiumStatus(userId)

      // Check AI rate limit (10 per day for free users only)
      const billingService = tryGetBillingService()
      if (!billingService && !isPremium) {
        const rateLimit = await this.checkAIRateLimit(userId)
        if (!rateLimit.allowed) {
          return ApiResponse.tooManyRequests(
            reply,
            `AI generation quota exceeded. Limit: 5 per day for free users. Upgrade to premium for unlimited access.`
          )
        }
        request.log.info({ userId, remaining: rateLimit.remaining }, 'AI rate limit check passed (free user)')
      }

      const plan = await this.service.generatePlan(userId, request.body)
      logger.info('AI plan generated successfully', {
        userId,
        planId: plan.id,
        stagesCount: plan.stages?.length ?? 0
      })
      return reply.code(201).send(plan)
    } catch (error) {
      logger.error('Failed to generate AI plan', { error })
      request.log.error(error, 'Failed to generate AI plan')
      return ApiResponse.internalError(reply)
    }
  }

  async updatePlan(
    request: FastifyRequest<{ Params: { id: string }; Body: UpdatePlanDTO }>,
    reply: FastifyReply
  ) {
    try {
      const { id } = request.params
      const userId = request.user?.uid
      logger.debug('Request: Update plan', { planId: id, userId })
      const existing = await this.service.getPlan(id)

      if (!existing) {
        logger.warn('Plan not found for update', { planId: id, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      if (!this.hasEditAccess(existing, userId)) {
        logger.warn('Forbidden plan update attempt', { planId: id, userId, ownerId: existing.userId })
        return ApiResponse.forbidden(reply)
      }

      // Sanitize input before saving
      const sanitizedData = sanitizePlanData(request.body)
      const updated = await this.service.updatePlan(id, sanitizedData)
      logger.info('Plan updated via controller', {
        planId: id,
        userId,
        updatedFields: Object.keys(request.body ?? {})
      })
      return reply.send(updated)
    } catch (error) {
      logger.error('Failed to update plan', { error })
      request.log.error(error, 'Failed to update plan')
      return ApiResponse.internalError(reply)
    }
  }

  async deletePlan(request: FastifyRequest<{ Params: { id: string } }>, reply: FastifyReply) {
    try {
      const { id } = request.params
      const userId = request.user?.uid
      logger.debug('Request: Delete plan', { planId: id, userId })
      const existing = await this.service.getPlan(id)

      if (!existing) {
        logger.warn('Plan not found for deletion', { planId: id, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      // 🔒 SECURITY: Only the OWNER can delete a plan (not editors/admins)
      if (!this.isOwner(existing, userId)) {
        logger.warn('Forbidden plan deletion attempt - not owner', { planId: id, userId, ownerId: existing.userId })
        return ApiResponse.forbidden(reply, 'Only the plan owner can delete this plan')
      }

      await this.service.deletePlan(id)
      logger.info('Plan deleted via controller', { planId: id, userId })
      return reply.code(204).send()
    } catch (error) {
      logger.error('Failed to delete plan', { error })
      request.log.error(error, 'Failed to delete plan')
      return ApiResponse.internalError(reply)
    }
  }

  async createStage(
    request: FastifyRequest<{ Params: { planId: string }; Body: CreateStageDTO }>,
    reply: FastifyReply
  ) {
    try {
      const { planId } = request.params
      const userId = request.user?.uid
      logger.debug('Request: Create stage', { planId, userId })
      const plan = await this.service.getPlan(planId)

      if (!plan) {
        logger.warn('Plan not found for stage creation', { planId, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      if (!this.hasEditAccess(plan, userId)) {
        logger.warn('Forbidden stage creation attempt', { planId, userId })
        return ApiResponse.forbidden(reply)
      }

      const billingService = tryGetBillingService()
      if (billingService && request.user?.uid) {
        const subscriptionPlan = await billingService.getUserPlan(request.user.uid)
        const limit = subscriptionPlan.limits.stopsPerPlan
        if (limit >= 0) {
          const currentCount = Array.isArray(plan.stages) ? plan.stages.length : 0
          if (currentCount >= limit) {
            logger.warn('Stage limit reached for subscription', {
              planId,
              userId,
              limit,
              currentCount
            })
            return ApiResponse.tooManyRequests(reply, 'Stop limit reached for this plan')
          }
        }
      } else {
        // Free user limit (no billing service)
        const MAX_FREE_STAGES = 200 // increased 10x
        const currentCount = Array.isArray(plan.stages) ? plan.stages.length : 0
        if (currentCount >= MAX_FREE_STAGES) {
          logger.warn('Stage limit reached for free tier', {
            planId,
            userId,
            limit: MAX_FREE_STAGES,
            currentCount
          })
          return ApiResponse.tooManyRequests(reply, `Maximum stages reached (${MAX_FREE_STAGES} for free users)`)
        }
      }

      // Sanitize input before saving
      const sanitizedData = sanitizeStageData(request.body)
      const stage = await this.service.createStage(planId, sanitizedData)
      logger.info('Stage created via controller', { planId, stageId: stage.id, userId })
      
      // Emit event for gamification
      if (userId) {
        appEvents.emit(AppEvent.STAGE_ADDED, { userId, planId, stageId: stage.id, timestamp: Date.now() })
      }
      
      return reply.code(201).send(stage)
    } catch (error) {
      logger.error('Failed to create stage', { error })
      request.log.error(error, 'Failed to create stage')
      return ApiResponse.internalError(reply)
    }
  }

  async updateStage(
    request: FastifyRequest<{ Params: { id: string }; Body: UpdateStageDTO }>,
    reply: FastifyReply
  ) {
    try {
      const { id } = request.params
      const userId = request.user?.uid
      logger.debug('Request: Update stage', { stageId: id, userId })
      
      // Get stage to check ownership
      const stage = await this.service.getStage(id)
      if (!stage) {
        logger.warn('Stage not found for update', { stageId: id, userId })
        return ApiResponse.notFound(reply, 'Stage not found')
      }

      // Check plan ownership
      const plan = await this.service.getPlan(stage.planId)
      if (!plan) {
        logger.warn('Plan not found for stage update', { stageId: id, userId, planId: stage.planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      if (!this.hasEditAccess(plan, userId)) {
        logger.warn('Forbidden stage update attempt', { stageId: id, userId, planId: stage.planId })
        return ApiResponse.forbidden(reply)
      }

      // Sanitize input before saving
      const sanitizedData = sanitizeStageData(request.body)
      const updated = await this.service.updateStage(id, sanitizedData)
      logger.info('Stage updated via controller', {
        stageId: id,
        planId: stage.planId,
        userId,
        updatedFields: Object.keys(request.body ?? {})
      })
      return reply.send(updated)
    } catch (error) {
      logger.error('Failed to update stage', { error })
      request.log.error(error, 'Failed to update stage')
      return ApiResponse.internalError(reply)
    }
  }

  async deleteStage(request: FastifyRequest<{ Params: { id: string } }>, reply: FastifyReply) {
    try {
      const { id } = request.params
      const userId = request.user?.uid
      logger.debug('Request: Delete stage', { stageId: id, userId })
      
      // Get stage to check ownership
      const stage = await this.service.getStage(id)
      if (!stage) {
        logger.warn('Stage not found for deletion', { stageId: id, userId })
        return ApiResponse.notFound(reply, 'Stage not found')
      }

      // Check plan ownership
      const plan = await this.service.getPlan(stage.planId)
      if (!plan) {
        logger.warn('Plan not found for stage deletion', { stageId: id, userId, planId: stage.planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      if (!this.hasEditAccess(plan, userId)) {
        logger.warn('Forbidden stage deletion attempt', { stageId: id, userId, planId: stage.planId })
        return ApiResponse.forbidden(reply)
      }

      await this.service.deleteStage(id)
      if (userId) {
        appEvents.emit(AppEvent.STAGE_REMOVED, {
          userId,
          planId: stage.planId,
          stageId: id,
          timestamp: Date.now()
        })
      }
      logger.info('Stage deleted via controller', { stageId: id, planId: stage.planId, userId })
      return reply.code(204).send()
    } catch (error) {
      logger.error('Failed to delete stage', { error })
      request.log.error(error, 'Failed to delete stage')
      return ApiResponse.internalError(reply)
    }
  }

  async reorderStages(
    request: FastifyRequest<{ Params: { planId: string }; Body: { stageIds: string[] } }>,
    reply: FastifyReply
  ) {
    try {
      const { planId } = request.params
      const userId = request.user?.uid
      const stageIds = request.body.stageIds ?? []
      logger.debug('Request: Reorder stages', { planId, userId, count: stageIds.length })

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const plan = await this.service.getPlan(planId)
      if (!plan) {
        logger.warn('Plan not found for ordering', { planId, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      if (!this.hasEditAccess(plan, userId)) {
        logger.warn('Forbidden stage reorder attempt', { planId, userId })
        return ApiResponse.forbidden(reply)
      }

      const reordered = await this.service.reorderStages(planId, stageIds)
      logger.info('Stages reordered via controller', { planId, userId, count: reordered.length })
      return reply.send(reordered)
    } catch (error) {
      logger.error('Failed to reorder stages', { error })
      request.log.error(error, 'Failed to reorder stages')
      return ApiResponse.internalError(reply)
    }
  }

  async optimizeRoute(
    request: FastifyRequest<{ Params: { planId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { planId } = request.params
      const userId = request.user?.uid
      logger.debug('Request: Optimize route', { planId, userId })
      const existing = await this.service.getPlan(planId)

      if (!existing) {
          logger.warn('Plan not found for route optimization', { planId, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      if (!this.hasEditAccess(existing, userId)) {
        logger.warn('Forbidden route optimization attempt', { planId, userId })
        return ApiResponse.forbidden(reply)
      }

      const optimized = await this.service.optimizeRoute(planId)
      logger.info('Route optimized via controller', { planId, userId, stages: optimized.length })
      return reply.send(optimized)
    } catch (error) {
      logger.error('Failed to optimize route', { error })
      request.log.error(error, 'Failed to optimize route')
      return ApiResponse.internalError(reply)
    }
  }

  async sharePlan(
    request: FastifyRequest<{ 
      Params: { id: string }
      Body: SharePlanDTO
    }>,
    reply: FastifyReply
  ) {
    try {
      const { id } = request.params
      const { userIds, permissions } = request.body
      const userId = request.user?.uid

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      logger.debug('Request: Share plan', { planId: id, userId, targetCount: userIds?.length ?? 0 })

      const existing = await this.service.getPlan(id)

      if (!existing) {
        logger.warn('Plan not found for share', { planId: id, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      if (existing.userId !== userId) {
        logger.warn('Forbidden plan share attempt', { planId: id, userId, ownerId: existing.userId })
        return ApiResponse.forbidden(reply, 'Only owners can share')
      }

      const updated = await this.service.sharePlan(id, userIds, permissions)
      logger.info('Plan shared via controller', {
        planId: id,
        userId,
        sharedWith: userIds,
        permissions
      })
      return reply.send(updated)
    } catch (error) {
      logger.error('Failed to share plan', { error })
      request.log.error(error, 'Failed to share plan')
      return ApiResponse.internalError(reply)
    }
  }

  async getSyncState(
    request: FastifyRequest<{ Params: { id: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { id } = request.params
      const userId = request.user?.uid

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      logger.debug('Request: Get sync state', { planId: id, userId })

      const plan = await this.service.getPlan(id)

      if (!plan) {
        logger.warn('Plan not found for sync state', { planId: id, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      // Check if user has access (owner, shared or collaborator)
      if (!this.hasViewAccess(plan, userId)) {
        logger.warn('Forbidden sync state access attempt', { planId: id, userId })
        return ApiResponse.forbidden(reply)
      }

      const state = await this.service.getSyncState(id)
      logger.info('Sync state returned', { planId: id, userId, hasState: Boolean(state) })
      return reply.send({ state })
    } catch (error) {
      logger.error('Failed to get sync state', { error })
      request.log.error(error, 'Failed to get sync state')
      return ApiResponse.internalError(reply)
    }
  }

  async updateSyncState(
    request: FastifyRequest<{ 
      Params: { id: string }
      Body: UpdateSyncStateDTO
    }>,
    reply: FastifyReply
  ) {
    try {
      const { id } = request.params
      const { state } = request.body
      const userId = request.user?.uid

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      logger.debug('Request: Update sync state', { planId: id, userId })

      const plan = await this.service.getPlan(id)

      if (!plan) {
        logger.warn('Plan not found for sync update', { planId: id, userId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      // Check if user has edit access (owner, collaborator or shared with edit permission)
      if (!this.hasEditAccess(plan, userId)) {
        logger.warn('Forbidden sync update attempt', { planId: id, userId })
        return ApiResponse.forbidden(reply, 'Edit access required')
      }

      await this.service.updateSyncState(id, state)
      logger.info('Sync state updated via controller', { planId: id, userId })
      return reply.code(204).send()
    } catch (error) {
      logger.error('Failed to update sync state', { error })
      request.log.error(error, 'Failed to update sync state')
      return ApiResponse.internalError(reply)
    }
  }
}
